import { Div, IRouteEvent, router, ldb, emitter } from '@codesuma/baseline'
import { HomeHeader } from './header'
import { Welcome, shouldShowWelcome } from './welcome'
import { PublicListsStrip } from './public-lists'
import { AddCircle, shouldShowAddCircle } from './add-circle'
import { ListCircle } from '../../components/list-circle'
import invites from '../../services/invites'
import { InstallPrompt, shouldShowInstallPrompt } from '../../components/install-prompt'
import { ThemeToggle } from '../../components/theme-toggle'
import listsService, { UserList } from '../../services/lists'
import services from '../../services'
import style from './index.module.css'

export const HomePage = () => {
    // Base container
    const base = Div()
    base.addClass(style.base)

    const header = HomeHeader()
    base.append(header)

    // Scrollable container of circles
    const scroller = Div()
    scroller.addClass(style.scroller)
    base.append(scroller)

    // Theme toggle, floated in the bottom-right corner
    base.append(ThemeToggle())

    const empty = Div('No lists yet — tap + to create one')
    empty.addClass(style.empty)


    const circles: Record<string, ReturnType<typeof ListCircle>> = {}
    let lastSig = '' // signature of the last render, to skip no-op rebuilds
    const SHOW_WELCOME = false // first-run welcome card parked for now
    let welcome: ReturnType<typeof Welcome> | null = null // current first-run card, if shown
    let publicStrip: ReturnType<typeof PublicListsStrip> | null = null // ready-made lists, below the circles
    let addCircle: ReturnType<typeof AddCircle> | null = null // "New list" hint circle, while the grid is small

    // Open a list — the exit handler turns this into the circle-expand transition
    const open = (item: UserList) => {
        router.goto('/list/' + item.id, { name: item.name })
    }

    const makeCircle = (item: UserList) => {
        const c = ListCircle(item)
        c.on('circle-open', open)
        circles[item.id] = c
        return c
    }

    // A list's identity for change detection — if every list's sig is unchanged we
    // can skip the rebuild, so the server refresh after the IndexedDB paint doesn't
    // flicker. Share state (role + member_count) is included so regrouping is caught;
    // picked color and pin state too, so a recolor repaints and a pin reorders.
    // The icon too: the server refresh is what brings another member's pick.
    const sig = (l: UserList) =>
        `${l.id}|${l.name}|${l.role}|${l.item_count ?? ''}|${l.done_count ?? ''}|${l.member_count ?? ''}|${l.shared_by ?? ''}|${l.color ?? ''}|${l.icon ?? ''}|${listsService.isPinned(l.id) ? 'p' : ''}`

    // TODO: temporary preview data — remove before shipping. One list per palette
    // color (the id determines the color), so all 14 circle colors are visible at
    // once; a few are shared to show the badge. Off now: first-run shows the 2 seeded
    // starter lists (see app/services/seed.ts) instead.
    const SHOW_DUMMIES = false
    const DUMMIES: UserList[] = [
        { id: 'dummy-5', name: 'Groceries', created_by: '', created_at: '', role: 'owner', item_count: 8 },
        { id: 'dummy-6', name: 'Travel Checklist', created_by: '', created_at: '', role: 'owner', item_count: 3, member_count: 2 },
        { id: 'dummy-7', name: 'Hardware', created_by: '', created_at: '', role: 'owner', item_count: 5 },
        { id: 'dummy-8', name: 'Weekend BBQ', created_by: '', created_at: '', role: 'owner', item_count: 12 },
        { id: 'dummy-9', name: 'Office', created_by: '', created_at: '', role: 'member', shared_by: 'Sara', item_count: 2 },
        { id: 'dummy-28', name: 'Camping', created_by: '', created_at: '', role: 'owner', item_count: 7 },
        { id: 'dummy-29', name: 'Birthday', created_by: '', created_at: '', role: 'owner', item_count: 4, member_count: 3 },
        { id: 'dummy-37', name: 'Garden', created_by: '', created_at: '', role: 'owner', item_count: 6 },
        { id: 'dummy-10', name: 'Pets', created_by: '', created_at: '', role: 'owner', item_count: 1 },
        { id: 'dummy-11', name: 'Travel', created_by: '', created_at: '', role: 'owner', item_count: 9 },
        { id: 'dummy-1', name: 'Books', created_by: '', created_at: '', role: 'member', shared_by: 'Max', item_count: 11 },
        { id: 'dummy-2', name: 'Gifts', created_by: '', created_at: '', role: 'owner', item_count: 5 },
        { id: 'dummy-3', name: 'Party', created_by: '', created_at: '', role: 'owner', item_count: 14, member_count: 4 },
        { id: 'dummy-4', name: 'Picnic', created_by: '', created_at: '', role: 'owner', item_count: 6 },
    ]

    const render = (lists: UserList[]) => {
        const all = SHOW_DUMMIES ? [...lists, ...DUMMIES] : lists

        // Skip the rebuild when nothing changed (the server fetch right after the
        // IndexedDB paint usually matches the cache) — avoids flicker.
        const fullSig = all.map(sig).join('||')
        if (fullSig === lastSig) return
        lastSig = fullSig

        for (const k in circles) delete circles[k]
        scroller.empty()

        // First-run greeting (explains the example lists), shown until dismissed.
        // Built fresh each render: scroller.empty() strips a reused instance's
        // children for good (baseline remove() is recursive and permanent).
        welcome = SHOW_WELCOME && shouldShowWelcome() ? Welcome() : null
        if (welcome) scroller.append(welcome)

        if (all.length === 0) scroller.append(empty)

        // All lists as circles in one wrapping grid (no group headings), with a
        // "New list" circle at the end while the grid is still small — a
        // first-timer shouldn't have to find the header's + to start.
        const grid = Div()
        grid.addClass(style.group)
        for (const item of all) grid.append(makeCircle(item))
        addCircle = shouldShowAddCircle(all.length) ? AddCircle() : null
        // if (addCircle) grid.append(addCircle)
        scroller.append(grid)

        // Ready-made public lists, below the user's own (and shown on an empty
        // install too — that's the best moment to offer a starting point). Built
        // fresh each render for the same reason as the welcome card above.
        publicStrip = PublicListsStrip()
        scroller.append(publicStrip)
    }

    // A list appeared or vanished elsewhere in the app — a saved copy of a
    // public list, or a delete. Refresh the grid even though this page was
    // never re-entered, so the new circle is there the moment you come back.
    listsService.on('change', (lists: UserList[]) => render(lists))

    // Incoming share invites: the invites service owns the overlay (it can
    // appear on any page, popped live by Realtime); re-render the circles
    // when a list is joined.
    invites.on('accepted', async () => {
        try {
            render(await listsService.getLists())
        } catch (err) {
            console.error('Failed to refresh lists after accepting invite:', err)
        }
    })

    // First-time web visitors: offer to install the native app / PWA (once).
    const maybeShowInstall = () => {
        if (!shouldShowInstallPrompt()) return
        const prompt = InstallPrompt()
        document.body.appendChild(prompt.el)
        prompt.show()
    }

    // Every circle on the page: the user's own, plus the public ones in the
    // strip, which open into their read-only page with the same morph.
    const allCircles = () => [...Object.values(circles), ...(publicStrip?.circles ?? [])]

    // Grow the tapped circle to fill the screen; scatter the rest
    const expand = (id: string) => {
        const target = allCircles().find(c => c.item.id === id)
        if (!target) return
        const rect = target.el.getBoundingClientRect()
        const cx = rect.left + rect.width / 2
        const cy = rect.top + rect.height / 2
        allCircles().forEach(c => c.expand(cx, cy, id))
    }

    const contract = () => {
        allCircles().forEach(c => c.contract())
    }

    // Theme flipped (from the bottom toggle): recolor the existing circles in place
    // so they pick up the dark/light palette without a rebuild.
    emitter.on('theme', () => {
        allCircles().forEach(c => c.recolor())
    })

    base.on('enter', async ({ from }: IRouteEvent) => {
        console.log('Home page enter, from:', from, 'at', Date.now());
        header.style({ transform: 'translateY(0)', opacity: '1'}, 200)
        // Bring the welcome card and ready-made strip back on the contract path
        // (a full re-render replaces them with fresh, unstyled instances anyway).
        welcome?.style({ transform: '', opacity: '' }, 200)
        setTimeout(() => publicStrip?.setChrome(true), 200)
        addCircle?.style({ transition: '', transform: '', opacity: '' }, 200)
        // transition:'' hands the timing back to the CSS (the slide exit above
        // overrides it with its own, faster curve).
        base.style({ transition: '', opacity: '1', pointerEvents: 'auto', transform: 'translateY(0)' })

        // Fresh app open (no previous page): if the last page was a list, open it
        // directly so the user lands where they left off.
        if (from === '') {
            const last = ldb.get('list:last')
            if (last?.id) {
                router.goto('/list/' + last.id, { name: last.name })
                return
            }
        }

        // Deliberately back on the lists screen — this is now the "last page", so
        // forget the remembered list and let the next fresh open land here.
        if (from && from.includes('/list/')) {
            ldb.remove('list:last')
        }

        // Back from a public list: its circle grew into that page, so bring
        // every circle back before the usual refresh (which skips a rebuild
        // when nothing changed, and would leave them scattered).
        if (from && from.includes('/l/')) contract()

        // Coming back from a list we have a circle for: contract the existing
        // circles instead of rebuilding them. Otherwise (deep link, a list just
        // created on the add page, one just deleted — gone from the cache — or a
        // pin toggled on the list page, which reorders the grid) fall through
        // and re-render fresh.
        const fromListId = from && from.includes('/list/') ? from.split('/').pop() : null
        if (fromListId && circles[fromListId] && listsService.getCachedName(fromListId)
            && circles[fromListId].pinned === listsService.isPinned(fromListId)) {
            // Items may have been ticked/added/removed on the list page — refresh the
            // returning circle's counts in place (this path skips a full re-render).
            const latest = listsService.getCached(fromListId)
            if (latest) circles[fromListId].update(latest)
            circles[fromListId].recolor() // a color picked on the list page applies in place
            contract()
            invites.check()
            return
        }

        // Paint instantly from the local cache, then refresh from the server.
        // The local paint must NOT wait on auth: offline, supabase's session
        // restore retries its token refresh for ~30s before initialization
        // resolves, and the home page would sit blank the whole time.
        try {
            render(await listsService.getLocalLists())
        } catch (err) {
            console.error('Failed to load local lists:', err)
        }

        // The server refresh does need the restored session's token.
        await services.supabase.auth.waitForInitialization()
        // if (!services.supabase.auth.isAuthenticated()) {
        //     router.goto('/app/login')
        //     return
        // }

        try {
            render(await listsService.getLists())
        } catch (err) {
            console.error('Failed to load lists:', err)
        }

        invites.check()
        maybeShowInstall()
    })

    base.on('exit', async ({ to }: IRouteEvent) => {
        if (to && (to.includes('/list/') || to.includes('/l/'))) {
            // Morph the tapped circle (one of ours, or a public one) into its page
            const id = to.split('/').pop() as string
            expand(id)
            header.style({ transform: 'translateY(-120%)', opacity: '0' })
            welcome?.style({ transform: 'translateY(-120%)', opacity: '0' })
            // The strip's circles scatter with ours; its own text fades.
            publicStrip?.setChrome(false)
            // Not a list circle, so expand() doesn't scatter it — shrink it away
            // in step with the ones that do.
            // transition:'' drops any press transition so the CSS morph timing applies
            addCircle?.style({ transition: '', transform: 'scale(0.4)', opacity: '0' })
            base.style({ opacity: '0', pointerEvents: 'none' }, 520)
        } else {
            // Match the shared page slide transition (components/page): home
            // clears upward on the same curve, and the router awaits this handler
            // — so holding here is what keeps the next page from rising in before
            // this one has finished leaving.
            base.style({ transition: 'opacity 0.2s ease, transform 0.2s ease', transform: 'translateY(-120px)', opacity: '0', pointerEvents: 'none' })
            await new Promise(resolve => setTimeout(resolve, 200))
        }
    })

    return base
}
